import styled, { css } from 'styled-components';

import { StepStatus } from './types';

/**
 * Container for the steps
 */
export const StepsContainer = styled.div<{
  /**
   * Whether the steps should be displayed horizontally
   */
  $isHorizontal?: boolean;
}>`
  display: flex;
  gap: 0.25rem;
  width: 100%;
  position: relative;

  ${(props) =>
    !props.$isHorizontal &&
    css`
      flex-direction: column;
      align-items: flex-start;
    `}

  ${(props) =>
    props.$isHorizontal &&
    css`
      align-items: center;
    `}
`;

/**
 * Container for a single step
 */
export const StepContainer = styled.div`
  display: flex;
  align-items: start;
  gap: 0.5rem;
`;

/**
 * Visual indicator for a step
 */
export const StepIndicator = styled.div<{
  /**
   * Status of the step
   */
  $status?: StepStatus;
  /**
   * Size of the indicator
   */
  $size?: 'small' | 'default';
  /**
   * Whether the step is active
   */
  $isActive?: boolean;
}>`
  display: flex;
  height: ${(props) => (props.$size === 'small' ? '1.5rem' : '2rem')};
  width: ${(props) => (props.$size === 'small' ? '1.5rem' : '2rem')};
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.$isActive ? '700' : '500')};
  position: relative;
  z-index: 2;
  box-shadow: 0 0 0 4px ${(props) => props.theme.colors.white};

  background-color: ${(props) =>
    props.$status === 'finish'
      ? props.theme.colors.success
      : props.$status === 'error'
        ? props.theme.colors.red3
        : props.$isActive
          ? props.theme.colors.gray3
          : props.theme.colors.gray1};
  color: ${(props) =>
    props.$status === 'finish'
      ? props.theme.colors.white
      : props.$status === 'error'
        ? props.theme.colors.danger
        : props.$isActive
          ? props.theme.colors.transcendNavy
          : props.theme.colors.transcendNavy3};
`;

/**
 * Container for step content (title and description)
 */
export const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.25rem;
`;

/**
 * Step title component
 */
export const StepTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

/**
 * Step description component
 */
export const StepDescription = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.transcendNavy3};
`;

/**
 * Divider between steps
 */
export const Divider = styled.div<{
  /**
   * Whether the divider should be horizontal
   */
  $isHorizontal?: boolean;
}>`
  flex-shrink: 0;
  background-color: ${(props) => props.theme.colors.gray3};
  z-index: 1;

  ${(props) =>
    props.$isHorizontal &&
    css`
      flex-grow: 1;
      height: 1px;
      min-width: 1rem;
      margin: 0 0.5rem;
    `}
`;
